import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AutocompleteDropdown,
  Box,
  Controller,
  ControllerWrapper,
  FieldWrapper,
  FormFieldsContainer,
  Label,
  Option,
  RegularField,
  useFormContext,
  useResponsive,
  ZipCodeInput,
} from '@common-fe/common-fe';

import { State } from '@/common/types';
import { ProviderForm, ProviderFormField } from "@/modules/UseMyMoney/widgets/FieldSetup/hooks/useProviderForm";

interface Props {
  states?: State[]
}

const ProviderModalForm = ({ states }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const {
    control,
    formState: { errors },
  } = useFormContext<ProviderForm>();

  const statesOptions: Option[] = useMemo(() => states?.map((state) => ({
    key: state.id,
    title: state.name,
  })) || [], [states]);

  return (
    <Box
      pad={isMobile ? 'spacing16' : { vertical: 'spacing24', horizontal: 'spacing32' }}
      background="canvas"
      round="container1Round"
    >
      <FormFieldsContainer>
        <FieldWrapper>
          <Label
            name={ProviderFormField.providerName}
            isNarrow
            showRequireIcon
          >
            {t('Name')}
          </Label>

          <ControllerWrapper errorText={errors?.[ProviderFormField.providerName]?.message}>
            <Controller
              name={ProviderFormField.providerName}
              control={control}
              render={({ field }) => (
                <RegularField
                  testId="add-provider-from_provider-name"
                  name={ProviderFormField.providerName}
                  value={field.value}
                  onChange={field.onChange}
                  editMode
                  placeholder={t('Enter name')}
                  isError={!!errors?.[ProviderFormField.providerName]?.message}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label
            name={ProviderFormField.addressLine1}
            isNarrow
            showRequireIcon
          >
            {t('Address Line 1')}
          </Label>

          <ControllerWrapper errorText={errors?.[ProviderFormField.addressLine1]?.message}>
            <Controller
              name={ProviderFormField.addressLine1}
              control={control}
              render={({ field }) => (
                <RegularField
                  testId="add-provider-from_address-line-1"
                  name={ProviderFormField.addressLine1}
                  value={field.value}
                  onChange={field.onChange}
                  editMode
                  placeholder={t('Address Line 1')}
                  isError={!!errors?.[ProviderFormField.addressLine1]?.message}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label
            name={ProviderFormField.addressLine2}
            isNarrow
          >
            {t('Address Line 2')}
          </Label>

          <ControllerWrapper errorText={errors?.[ProviderFormField.addressLine2]?.message}>
            <Controller
              name={ProviderFormField.addressLine2}
              control={control}
              render={({ field }) => (
                <RegularField
                  testId="add-provider-from_address-line-2"
                  name={ProviderFormField.addressLine2}
                  value={field.value}
                  onChange={field.onChange}
                  editMode
                  placeholder={t('Address Line 2')}
                  isError={!!errors?.[ProviderFormField.addressLine2]?.message}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label
            name={ProviderFormField.city}
            isNarrow
            showRequireIcon
          >
            {t('City')}
          </Label>

          <ControllerWrapper errorText={errors?.[ProviderFormField.city]?.message}>
            <Controller
              name={ProviderFormField.city}
              control={control}
              render={({ field }) => (
                <RegularField
                  testId="add-provider-from_city"
                  name={ProviderFormField.city}
                  value={field.value}
                  onChange={field.onChange}
                  editMode
                  placeholder={t('Enter City')}
                  isError={!!errors?.[ProviderFormField.city]?.message}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label
            name={ProviderFormField.state}
            isNarrow
            showRequireIcon
          >
            {t('State')}
          </Label>

          <ControllerWrapper>
            <Controller
              name={ProviderFormField.state}
              control={control}
              render={({ field }) => (
                <AutocompleteDropdown
                  testId="add-provider-state"
                  name={ProviderFormField.state}
                  value={field.value}
                  onChange={field.onChange}
                  options={statesOptions}
                  placeholder={t('Select state')}
                  errorText={errors?.[ProviderFormField.state]?.message}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label
            name={ProviderFormField.zipCode}
            isNarrow
            showRequireIcon
          >
            {t('ZIP code')}
          </Label>

          <ControllerWrapper errorText={errors?.[ProviderFormField.zipCode]?.message}>
            <Controller
              name={ProviderFormField.zipCode}
              control={control}
              render={({ field }) => (
                <ZipCodeInput
                  testId="add-provider-zip-code"
                  name={ProviderFormField.zipCode}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('Zip')}
                  error={!!errors?.[ProviderFormField.zipCode]?.message}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>
      </FormFieldsContainer>
    </Box>
  );
};

export default ProviderModalForm;
